import type { PublicGalleryPhoto } from './public-photo.models';

// Order the stored renditions fall back through. The 640 rung is legacy: the shared processing
// service no longer produces it, but photos cut before that still carry it, so it stays a valid
// fallback while never being a pick. Large (1920) is never a fallback: only a surface whose ceiling
// is `large` (hero cover, viewer) asks for it, so a grid tile never climbs to it.
const FALLBACK_ORDER = ['thumb', 'small', 'medium'] as const;
const URL_ORDER = [...FALLBACK_ORDER, 'large'] as const;

export type PreviewVersionKey = (typeof URL_ORDER)[number];

// The rungs a surface chooses between, nominal LONG side (the service cuts 320 / 1280 / 1920). A
// rung's horizontal width equals the long side for a landscape frame and shrinks with the ratio for
// a portrait one. Headroom: thumb upscaled by up to 1,4× still reads sharp and saves far more bytes
// than the sharpness it costs; medium gets none, so a surface that may take large never upscales
// medium (on a grid medium is the ceiling, its headroom never decides anything).
const PICK_RUNGS = [
  { key: 'thumb', longSide: 320, tolerance: 1.4 },
  { key: 'medium', longSide: 1280, tolerance: 1 },
  { key: 'large', longSide: 1920, tolerance: 1 },
] as const satisfies readonly { key: PreviewVersionKey; longSide: number; tolerance: number }[];

/**
 * @param cssWidth width of the cell the frame renders into
 * @param dpr device pixel ratio, capped at 2
 * @param aspectRatio width / height of the photo; unknown frames are treated as 3:2 landscape
 * @param ceiling top rung this surface may take — grid tiles stop at medium, hero cover and viewer at large
 */
export function previewVersionFor(
  cssWidth: number,
  dpr: number,
  aspectRatio = 1.5,
  ceiling: 'medium' | 'large' = 'medium',
): PreviewVersionKey {
  const target = cssWidth * Math.min(dpr, 2);
  for (const rung of PICK_RUNGS) {
    if (rung.key === ceiling) return ceiling;
    // Portrait: the long side is the HEIGHT, so the rendition is narrower than the rung's nominal number.
    const horizontal = aspectRatio >= 1 ? rung.longSide : rung.longSide * aspectRatio;
    if (horizontal * rung.tolerance >= target) return rung.key;
  }

  return ceiling;
}

export function pickPreviewUrl(photo: PublicGalleryPhoto, version: PreviewVersionKey): string | null {
  return pickVersionUrl(photo.urls, version);
}

export function pickVersionUrl(urls: PublicGalleryPhoto['urls'], version: PreviewVersionKey): string | null {
  // A payload without the large tier (older API, cabinet, portfolio) serves exactly what medium serves.
  if (version === 'large') return urls.large ?? pickVersionUrl(urls, 'medium');
  // Fall upward: a missing smaller version is served by the next larger one.
  for (let i = FALLBACK_ORDER.indexOf(version); i < FALLBACK_ORDER.length; i++) {
    const url = urls[FALLBACK_ORDER[i]];
    if (url) return url;
  }

  return null;
}

export function isVersionUpgrade(current: PreviewVersionKey | null, next: PreviewVersionKey): boolean {
  if (current === null) return true;
  return URL_ORDER.indexOf(next) > URL_ORDER.indexOf(current);
}
